"use client";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

type SliderProps = {
    children: React.ReactNode;
    items?: number;
    bullet?: boolean;
    thumbnails?: React.ReactNode[];
};

export default function Slider({
                                   children,
                                   items = 4,
                                   bullet = false,
                                   thumbnails
                               }: SliderProps) {
    const sliderRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const slides = React.Children.toArray(children);
    const itemClass = useMemo(() => {
        switch (items) {
            case 1:
                return "w-full";
            case 2:
                return "w-1/2";
            case 3:
                return "w-1/2 lg:w-1/3";
            case 4:
                return "w-1/2 lg:w-1/4";
            case 5:
                return "w-1/2 lg:w-1/5";
            case 6:
                return "w-1/3 lg:w-1/6";
            case 7:
                return "w-1/3 lg:w-[14.285714%]";
            case 8:
                return "w-1/3 lg:w-1/8";
            default:
                return "w-1/2 lg:w-1/4";
        }
    }, [items]);

    const updateButtons = useCallback(() => {
        const container = sliderRef.current;
        if (!container) return;
        const slide =
            container.firstElementChild as HTMLElement | null;
        const slideWidth = slide?.offsetWidth || 1;
        const scrollPosition = Math.abs(container.scrollLeft);
        const index = Math.round(
            scrollPosition / slideWidth
        );
        setActiveIndex(index);
    }, []);

    const handleScroll = useCallback(
        (direction: "next" | "prev") => {
            const container = sliderRef.current;
            if (!container) return;
            const slide =
                container.firstElementChild as HTMLElement | null;
            const slideWidth = slide?.offsetWidth || 0;
            container.scrollBy({
                left:
                    direction === "next"
                        ? slideWidth
                        : -slideWidth,
                behavior: "smooth",
            });
        },
        []
    );

    const handleBulletClick = (index: number) => {
        const container = sliderRef.current;
        if (!container) return;
        const slide =
            container.children[index] as HTMLElement | null;
        if (!slide) return;
        slide.scrollIntoView({
            behavior: "smooth",
            inline: "start",
            block: "nearest",
        });
        setActiveIndex(index);
    };

    useEffect(() => {
        const container = sliderRef.current;
        if (!container) return;
        updateButtons();
        container.addEventListener("scroll", updateButtons, {
            passive: true,
        });
        window.addEventListener("resize", updateButtons);
        return () => {
            container.removeEventListener(
                "scroll",
                updateButtons
            );
            window.removeEventListener(
                "resize",
                updateButtons
            );
        };
    }, [updateButtons]);

    const showArrows = slides.length > items;

    return (
        <div className="relative w-full">

            <div
                ref={sliderRef}
                className="flex overflow-x-auto sm:overflow-x-hidden scroll-smooth scrollbar-hide py-1"
            >
                {slides.map((child, i) => (
                    <div
                        key={i}
                        dir="rtl"
                        className={`${itemClass} flex-shrink-0 px-1 h-auto flex flex-col`}
                    >
                        {child}
                    </div>
                ))}
            </div>

            {showArrows && (
                <>
                    <button
                        onClick={() => handleScroll("prev")}
                        className="absolute top-1/2 sm:-left-5 left-0 -translate-y-1/2 rounded-lg bg-white p-2 shadow transition cursor-pointer z-10"
                        title={'اسلاید بعدی'}
                    >
                        <ChevronLeft
                            size={20}
                            className={"text-blue-600 dark:text-white"}
                        />
                    </button>
                    <button
                        onClick={() => handleScroll("next")}
                        className="absolute top-1/2 sm:-right-5 right-0 -translate-y-1/2 rounded-lg bg-white p-2 shadow transition cursor-pointer z-10"
                        title={'اسلاید قبلی'}
                    >
                        <ChevronRight
                            size={20}
                            className={"text-blue-600 dark:text-white"}
                        />
                    </button>
                </>
            )}

            {thumbnails && thumbnails.length > 1 ? (
                <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                    {thumbnails.map((thumb, index) => (
                        <button
                            key={index}
                            onClick={() => handleBulletClick(index)}
                            className={`overflow-hidden rounded-md border-2 transition-all cursor-pointer ${
                                activeIndex === index
                                    ? "border-blue-600"
                                    : "border-gray-200"
                            }`}
                        >
                            {thumb}
                        </button>
                    ))}
                </div>
            ) : (
                bullet &&
                slides.length > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => handleBulletClick(index)}
                                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                                    activeIndex === index
                                        ? "bg-blue-600 scale-125"
                                        : "bg-gray-300"
                                }`}
                            />
                        ))}
                    </div>
                )
            )}

        </div>
    );
}